import { useAuth } from "@/context/AuthContext";
import { Info } from "lucide-react";

/**
 * RoleGuide — small "what you should do on this page" banner shown per role.
 * Pass an object keyed by role. Roles not listed will not see the banner.
 *
 * Example:
 *   <RoleGuide guides={{
 *     filler: { title: "Punch your shift", text: "Once per shift", freq: "1×/shift" },
 *     cashier: { title: "Close cash", text: "At handover", freq: "1×/shift" },
 *   }} />
 */
export default function RoleGuide({ guides, defaultForAll }) {
  const { user } = useAuth();
  const role = user?.role;
  const g = guides?.[role] || (defaultForAll ? { title: defaultForAll.title, text: defaultForAll.text, freq: defaultForAll.freq } : null);
  if (!g) return null;

  const roleColour = {
    filler: "border-emerald-500 bg-emerald-50 text-emerald-900",
    cashier: "border-blue-500 bg-blue-50 text-blue-900",
    assistant_manager: "border-purple-500 bg-purple-50 text-purple-900",
    manager: "border-indigo-500 bg-indigo-50 text-indigo-900",
    admin: "border-slate-500 bg-slate-50 text-slate-900",
    owner: "border-amber-500 bg-amber-50 text-amber-900",
  }[role] || "border-slate-300 bg-slate-50 text-slate-800";

  const roleLabel = {
    filler: "Filler",
    cashier: "Cashier",
    assistant_manager: "Supervisor",
    manager: "Manager",
    admin: "Admin",
    owner: "Owner",
  }[role] || role;

  return (
    <div className={"border-l-4 border-2 rounded-sm p-3 " + roleColour} data-testid="role-guide-banner">
      <div className="flex items-start gap-2">
        <Info size={16} className="shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider font-bold opacity-80">As a {roleLabel}</span>
            {g.freq && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-white border border-current rounded-sm">{g.freq}</span>}
          </div>
          <div className="font-bold text-sm mt-0.5">{g.title}</div>
          <div className="text-xs opacity-90 mt-0.5">{g.text}</div>
        </div>
      </div>
    </div>
  );
}
